import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/seo";
import { FiTrendingDown, FiClock, FiDollarSign, FiArrowRight } from "@/lib/icons";
import { ToggleGroup } from "./_components/ToggleGroup";
import {
  useExtraRepaymentCalculator,
  type ExtraFrequency,
} from "./_hooks/useExtraRepaymentCalculator";
import { cn } from "@/lib/utils";

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatYearsMonths(months: number): string {
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  if (years === 0) return `${remainingMonths} months`;
  if (remainingMonths === 0) return `${years} years`;
  return `${years} yrs ${remainingMonths} mo`;
}

function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step,
  formatValue,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  formatValue: (v: number) => string;
}) {
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label className="text-sm font-medium">{label}</label>
        <span className="text-lg font-bold text-primary">{formatValue(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
      />
    </div>
  );
}

export function ExtraRepaymentCalculatorPage() {
  const { inputs, results, updateInput } = useExtraRepaymentCalculator();

  const frequencyOptions: { value: ExtraFrequency; label: string }[] = [
    { value: "weekly", label: "Weekly" },
    { value: "fortnightly", label: "Fortnightly" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  const extraPresets = [100, 200, 500, 1000];

  return (
    <>
      <SEO
        title="Extra Repayment Calculator"
        description="See how much time and interest you could save by making extra repayments on your home loan."
        url="/calculators/extra-repayments"
      />

      <section className="bg-cream py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex p-3 bg-accent/20 rounded-xl mb-6">
              <FiTrendingDown className="size-6 text-primary" />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
              Extra Repayment Calculator
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See how much time and interest you could save by making extra repayments
              on your home loan. Even small amounts can make a big difference!
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Inputs */}
            <div className="bg-background rounded-3xl border border-border p-8">
              <h2 className="font-serif text-xl mb-8">Your Loan Details</h2>

              <div className="space-y-8">
                <SliderInput
                  label="Loan Amount"
                  value={inputs.loanAmount}
                  onChange={(v) => updateInput("loanAmount", v)}
                  min={100000}
                  max={2000000}
                  step={10000}
                  formatValue={formatCurrency}
                />

                <SliderInput
                  label="Interest Rate"
                  value={inputs.interestRate}
                  onChange={(v) => updateInput("interestRate", v)}
                  min={3}
                  max={10}
                  step={0.1}
                  formatValue={(v) => `${v.toFixed(2)}%`}
                />

                <SliderInput
                  label="Loan Term"
                  value={inputs.loanTerm}
                  onChange={(v) => updateInput("loanTerm", v)}
                  min={5}
                  max={30}
                  step={1}
                  formatValue={(v) => `${v} years`}
                />

                <div className="border-t border-border pt-8">
                  <h3 className="font-medium mb-6">Extra Repayments</h3>

                  <div className="space-y-6">
                    <SliderInput
                      label="Extra Amount"
                      value={inputs.extraAmount}
                      onChange={(v) => updateInput("extraAmount", v)}
                      min={0}
                      max={2000}
                      step={25}
                      formatValue={formatCurrency}
                    />

                    <div className="flex flex-wrap gap-2">
                      {extraPresets.map((preset) => (
                        <button
                          key={preset}
                          onClick={() => updateInput("extraAmount", preset)}
                          className={cn(
                            "px-3 py-1.5 text-xs font-medium rounded-lg transition-colors",
                            inputs.extraAmount === preset
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground hover:bg-muted/80"
                          )}
                        >
                          {formatCurrency(preset)}
                        </button>
                      ))}
                    </div>

                    <div className="space-y-3">
                      <label className="text-sm font-medium">Frequency</label>
                      <ToggleGroup
                        options={frequencyOptions}
                        value={inputs.extraFrequency}
                        onChange={(v) => updateInput("extraFrequency", v)}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-border pt-8">
                  <h3 className="font-medium mb-6">Lump Sum Payment (Optional)</h3>
                  <div className="space-y-6">
                    <SliderInput
                      label="Lump Sum Amount"
                      value={inputs.lumpSum}
                      onChange={(v) => updateInput("lumpSum", v)}
                      min={0}
                      max={100000}
                      step={5000}
                      formatValue={formatCurrency}
                    />
                    {inputs.lumpSum > 0 && (
                      <SliderInput
                        label="In Year"
                        value={inputs.lumpSumYear}
                        onChange={(v) => updateInput("lumpSumYear", v)}
                        min={1}
                        max={10}
                        step={1}
                        formatValue={(v) => `Year ${v}`}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Results Comparison */}
            <div className="space-y-6">
              {/* Without Extra */}
              <div className="bg-muted/50 rounded-2xl p-6">
                <h3 className="font-medium text-muted-foreground mb-4">Without Extra Repayments</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Monthly Payment</p>
                    <p className="text-xl font-bold">{formatCurrency(results.originalMonthlyPayment)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Loan Term</p>
                    <p className="text-xl font-bold">{formatYearsMonths(results.originalTermMonths)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Interest</p>
                    <p className="text-xl font-bold text-muted-foreground">
                      {formatCurrency(results.originalTotalInterest)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Repayments</p>
                    <p className="text-xl font-bold">{formatCurrency(results.originalTotalRepayments)}</p>
                  </div>
                </div>
              </div>

              {/* With Extra */}
              <div className="bg-accent/10 rounded-2xl p-6 ring-2 ring-accent/30">
                <h3 className="font-medium text-primary mb-4">With Extra Repayments</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Monthly Payment</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(results.newMonthlyPayment + results.effectiveExtraMonthly)}
                    </p>
                    <p className="text-xs text-accent">
                      +{formatCurrency(results.effectiveExtraMonthly)} extra
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Loan Term</p>
                    <p className="text-xl font-bold text-accent">
                      {formatYearsMonths(results.newTermMonths)}
                    </p>
                    {results.timeSavedMonths > 0 && (
                      <p className="text-xs text-accent">
                        {formatYearsMonths(results.timeSavedMonths)} faster!
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Interest</p>
                    <p className="text-xl font-bold text-accent">
                      {formatCurrency(results.newTotalInterest)}
                    </p>
                    {results.interestSaved > 0 && (
                      <p className="text-xs text-accent">
                        Save {formatCurrency(results.interestSaved)}!
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Repayments</p>
                    <p className="text-xl font-bold">{formatCurrency(results.newTotalRepayments)}</p>
                  </div>
                </div>
              </div>

              {/* Visual Comparison */}
              <div className="bg-background rounded-2xl border border-border p-6">
                <h3 className="font-medium mb-4">Interest Comparison</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Original</span>
                      <span className="text-muted-foreground">{formatCurrency(results.originalTotalInterest)}</span>
                    </div>
                    <div className="h-6 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-muted-foreground/40 rounded-full" style={{ width: "100%" }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>With Extra</span>
                      <span className="text-accent">{formatCurrency(results.newTotalInterest)}</span>
                    </div>
                    <div className="h-6 bg-accent/20 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-accent rounded-full transition-all duration-500"
                        style={{
                          width: `${(results.newTotalInterest / results.originalTotalInterest) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-primary text-primary-foreground rounded-2xl p-6 text-center">
                <p className="text-primary-foreground/90 mb-2">Want to pay off your loan faster?</p>
                <p className="font-serif text-xl mb-4">Let's find you a better rate</p>
                <Button asChild className="btn-pill bg-accent text-accent-foreground hover:bg-accent/90">
                  <Link to="/contact">
                    Get Started
                    <FiArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Savings Highlight - placed after inputs */}
          {results.interestSaved > 0 && (
            <div className="bg-primary text-primary-foreground rounded-3xl p-8 mt-12 max-w-3xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8 text-center">
                <div>
                  <FiDollarSign className="size-8 mx-auto mb-2 opacity-80" />
                  <p className="text-primary-foreground/90 text-sm mb-1">Interest Saved</p>
                  <p className="text-4xl font-bold">{formatCurrency(results.interestSaved)}</p>
                </div>
                <div>
                  <FiClock className="size-8 mx-auto mb-2 opacity-80" />
                  <p className="text-primary-foreground/90 text-sm mb-1">Time Saved</p>
                  <p className="text-4xl font-bold">{formatYearsMonths(results.timeSavedMonths)}</p>
                </div>
              </div>
              <p className="text-center text-primary-foreground/90 text-sm mt-6">
                By paying an extra {formatCurrency(results.effectiveExtraMonthly)}/month
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-6">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> This calculator provides estimates only. Check with your lender
              about any fees for extra repayments or early payout. Some loans have limits on extra
              repayments, especially fixed rate loans.
            </p>
            <p className="text-sm text-muted-foreground">
              <strong>Disclaimer:</strong> This page provides general information only and has been
              prepared without considering your objectives, financial situation or needs. We recommend
              that you consider whether it is appropriate for your circumstances and your full financial
              situation will need to be reviewed prior to acceptance of any offer or product. It does
              not constitute legal, tax or financial advice and you should always seek professional
              advice in relation to your individual circumstances.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
